import { solveTower, towerStarDrops, TowerModel, type TowerRules } from "@nano/puzzle-core";
import { z } from "zod";
import { TOWER_MARBLE_BY_ID } from "./palette.ts";

export const LOGIC_HOUSE_ID = "logic" as const;

/** Phones keep a cell at 56 px or more up to this many columns and rows. */
export const TOWER_LIMITS = { columns: 5, rows: 6, cup: 4 } as const;

const cellToken = "(?:\\.|<|>|[LR][1-9]?[a-d]?)";

export const towerRulesSchema = z
  .object({
    rows: z
      .array(z.string().regex(new RegExp(`^${cellToken}( ${cellToken})*$`)))
      .min(1)
      .max(TOWER_LIMITS.rows),
    colors: z
      .array(z.string().refine((id) => TOWER_MARBLE_BY_ID.has(id), "unknown marble colour"))
      .min(1)
      .max(4),
    cups: z
      .array(z.string().regex(/^(?:-|[a-d][1-4](?:\/[a-d])?)$/))
      .min(3)
      .max(TOWER_LIMITS.columns),
    hoppers: z
      .array(z.string().regex(/^[a-d]*$/))
      .min(1)
      .max(2),
  })
  .strict();

export const logicLevelSchema = z
  .object({
    id: z.number().int().min(1).max(100),
    houseId: z.literal(LOGIC_HOUSE_ID),
    contentVersion: z.string().regex(/^logic-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]),
    themeId: z.string().min(1),
    tutorialId: z.string().min(1).optional(),
    /** Fewest drops, proven by the solver when the level was frozen. */
    optimalDrops: z.number().int().min(1),
    threeStarDrops: z.number().int().min(1),
    twoStarDrops: z.number().int().min(1),
    seed: z.number().int().nonnegative().optional(),
    rules: towerRulesSchema,
  })
  .strict();

export type LogicLevel = z.infer<typeof logicLevelSchema>;

export const logicPackSchema = z
  .object({
    packVersion: z.literal(1),
    houseId: z.literal(LOGIC_HOUSE_ID),
    contentVersion: z.string().regex(/^logic-\d+\.\d+\.\d+$/),
    chapter: z.number().int().min(1).max(10),
    levels: z.array(logicLevelSchema).length(10),
  })
  .strict();

export type LogicPack = z.infer<typeof logicPackSchema>;

export function toTowerRules(level: LogicLevel): TowerRules {
  return level.rules as TowerRules;
}

/** Checks a level against the rules and the solver: problems, or an empty list. */
export function validateLogicLevel(level: LogicLevel, options: { solve?: boolean } = {}): string[] {
  const problems: string[] = [];
  let model: TowerModel;
  try {
    model = new TowerModel(toTowerRules(level));
  } catch (error) {
    return [`level ${level.id}: ${(error as Error).message}`];
  }
  const used = new Set(model.totals.keys());
  if (level.rules.colors.some((color) => !used.has(color))) problems.push(`level ${level.id}: lists a colour it never uses`);
  if (model.cups.some((cup) => cup && cup.capacity > TOWER_LIMITS.cup)) problems.push(`level ${level.id}: a cup holds more than ${TOWER_LIMITS.cup}`);
  if (level.rules.rows.some((row) => row.split(" ").every((cell) => cell === "."))) problems.push(`level ${level.id}: an empty row`);
  const stars = towerStarDrops(level.optimalDrops);
  if (stars.threeStarDrops !== level.threeStarDrops || stars.twoStarDrops !== level.twoStarDrops) {
    problems.push(`level ${level.id}: star thresholds should be ${stars.threeStarDrops} and ${stars.twoStarDrops}`);
  }
  if (options.solve !== false) {
    const solved = solveTower(model, { maxNodes: 2_000_000 });
    if (solved.status !== "solved") problems.push(`level ${level.id}: solver says ${solved.status}`);
    else if (solved.optimalDrops !== level.optimalDrops) problems.push(`level ${level.id}: optimal is ${solved.optimalDrops}, data says ${level.optimalDrops}`);
  }
  return problems;
}
